import { useState, useEffect, useRef } from 'react';
import {
  Mail, Send, ArrowLeft, MessageSquare, User, Clock, CheckCircle,
  Plus, Building2, Trash2, CheckCheck, ChevronDown, ChevronUp, Search, X
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  company_name: string;
  title?: string;
  phone?: string;
}

interface AdminMessage {
  id: string;
  to_client_id: string;
  subject: string;
  message: string;
  read: boolean;
  read_at: string | null;
  created_at: string;
  client?: Client;
  _source: 'admin_out';
}

interface MessageThread {
  id: string;
  workday_customer_id: string;
  subject: string;
  status: string;
  last_message_at: string;
  created_at: string;
  client_name?: string;
  client_email?: string;
  client_title?: string;
  client_phone?: string;
  client_company?: string;
  unread_count: number;
  last_message_preview: string;
  last_sender_type: string;
  _source: 'thread';
}

type InboxItem = AdminMessage | MessageThread;

interface ThreadMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  sender_type: string;
  message_body: string;
  is_read: boolean;
  created_at: string;
}

type PanelMode = 'list' | 'thread-detail' | 'admin-detail' | 'compose';

export default function ClientMessagingHub() {
  const { user } = useAuth();
  const [adminMessages, setAdminMessages] = useState<AdminMessage[]>([]);
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<PanelMode>('list');
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(null);
  const [expandedAdminMsg, setExpandedAdminMsg] = useState<AdminMessage | null>(null);
  const [threadMessages, setThreadMessages] = useState<ThreadMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newToClient, setNewToClient] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAll();
    fetchClients();

    const channel = supabase
      .channel('admin-messaging-hub')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'admin_messages' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_threads' }, fetchAll)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'thread_messages' }, (payload) => {
        if (selectedThread && payload.new && (payload.new as any).thread_id === selectedThread.id) {
          fetchThreadMessages(selectedThread.id);
        }
        fetchAll();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current && mode === 'thread-detail') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadMessages, mode]);

  const fetchAll = async () => {
    await Promise.all([fetchAdminMessages(), fetchThreads()]);
    setLoading(false);
  };

  const fetchClients = async () => {
    const { data } = await supabase
      .from('workday_client_profiles')
      .select('id, first_name, last_name, email, title, phone, company:companies(name)')
      .order('first_name', { ascending: true });
    if (data) {
      setClients(data.map((c: any) => ({
        ...c,
        company_name: c.company?.name || '',
      })));
    }
  };

  const fetchAdminMessages = async () => {
    const { data } = await supabase
      .from('admin_messages')
      .select(`
        *,
        client:workday_client_profiles!to_client_id(
          first_name, last_name, email, title, phone,
          company:companies(name)
        )
      `)
      .order('created_at', { ascending: false });
    if (data) {
      setAdminMessages(data.map((m: any) => ({
        ...m,
        _source: 'admin_out' as const,
        client: m.client ? {
          ...m.client,
          company_name: m.client.company?.name || '',
        } : undefined,
      })));
    }
  };

  const fetchThreads = async () => {
    const { data } = await supabase
      .from('message_threads')
      .select('*')
      .order('last_message_at', { ascending: false });
    if (!data) return;

    const enriched = await Promise.all(data.map(async (thread) => {
      const { data: clientProfile } = await supabase
        .from('workday_client_profiles')
        .select('first_name, last_name, title, phone, email, company:companies(name)')
        .eq('id', thread.workday_customer_id)
        .maybeSingle();

      const { data: lastMsg } = await supabase
        .from('thread_messages')
        .select('message_body, sender_type')
        .eq('thread_id', thread.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { count: unread } = await supabase
        .from('thread_messages')
        .select('*', { count: 'exact', head: true })
        .eq('thread_id', thread.id)
        .eq('sender_type', 'client')
        .eq('is_read', false);

      let clientName = 'Unknown Client';
      let clientTitle: string | undefined;
      let clientPhone: string | undefined;
      let clientCompany: string | undefined;
      let clientEmail: string | undefined;

      if (clientProfile) {
        clientName = `${clientProfile.first_name} ${clientProfile.last_name}`;
        clientTitle = clientProfile.title;
        clientPhone = clientProfile.phone;
        clientCompany = (clientProfile as any).company?.name;
        clientEmail = clientProfile.email;
      }

      return {
        ...thread,
        _source: 'thread' as const,
        client_name: clientName,
        client_title: clientTitle,
        client_phone: clientPhone,
        client_company: clientCompany,
        client_email: clientEmail,
        last_message_preview: lastMsg?.message_body || '',
        last_sender_type: lastMsg?.sender_type || 'client',
        unread_count: unread || 0,
      };
    }));

    setThreads(enriched);
  };

  const fetchThreadMessages = async (threadId: string) => {
    setLoadingMessages(true);
    const { data } = await supabase
      .from('thread_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });

    if (data) {
      setThreadMessages(data);
      const unread = data.filter(m => m.sender_type === 'client' && !m.is_read);
      for (const msg of unread) {
        await supabase.from('thread_messages').update({ is_read: true }).eq('id', msg.id);
      }
      fetchThreads();
    }
    setLoadingMessages(false);
  };

  const openThread = (thread: MessageThread) => {
    setSelectedThread(thread);
    fetchThreadMessages(thread.id);
    setMode('thread-detail');
  };

  const openAdminMsg = (msg: AdminMessage) => {
    setExpandedAdminMsg(msg);
    setMode('admin-detail');
  };

  const toggleRowExpand = (key: string) => {
    setExpandedRows(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const handleReply = async () => {
    if (!selectedThread || !replyMessage.trim()) return;
    setSending(true);
    const { data: { user: u } } = await supabase.auth.getUser();
    if (!u) { setSending(false); return; }

    const { error } = await supabase.from('thread_messages').insert({
      thread_id: selectedThread.id,
      sender_id: u.id,
      sender_type: 'admin',
      message_body: replyMessage,
    });

    if (!error) {
      setReplyMessage('');
      fetchThreadMessages(selectedThread.id);
    }
    setSending(false);
  };

  const handleToggleStatus = async (thread: MessageThread) => {
    const newStatus = thread.status === 'open' ? 'closed' : 'open';
    await supabase.from('message_threads').update({ status: newStatus }).eq('id', thread.id);
    if (selectedThread?.id === thread.id) {
      setSelectedThread({ ...thread, status: newStatus });
    }
    fetchThreads();
  };

  const handleSendDirect = async () => {
    if (!newToClient || !newSubject.trim() || !newMessage.trim()) return;
    setSending(true);
    const { error } = await supabase.from('admin_messages').insert({
      from_admin_id: user?.id,
      to_client_id: newToClient,
      subject: newSubject,
      message: newMessage,
    });
    if (!error) {
      setNewToClient('');
      setNewSubject('');
      setNewMessage('');
      fetchAdminMessages();
      setMode('list');
    }
    setSending(false);
  };

  const deleteAdminMsg = async (id: string) => {
    await supabase.from('admin_messages').delete().eq('id', id);
    setExpandedRows(prev => { const n = new Set(prev); n.delete(`admin-${id}`); return n; });
    if (expandedAdminMsg?.id === id) {
      setExpandedAdminMsg(null);
      setMode('list');
    }
    fetchAdminMessages();
  };

  const goBackToList = () => {
    setMode('list');
    setSelectedThread(null);
    setExpandedAdminMsg(null);
    setReplyMessage('');
    setNewSubject('');
    setNewMessage('');
    setNewToClient('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateFull = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const allItems: InboxItem[] = [
    ...adminMessages,
    ...threads,
  ].sort((a, b) => {
    const aDate = a._source === 'admin_out' ? a.created_at : (a as MessageThread).last_message_at || a.created_at;
    const bDate = b._source === 'admin_out' ? b.created_at : (b as MessageThread).last_message_at || b.created_at;
    return new Date(bDate).getTime() - new Date(aDate).getTime();
  });

  const filteredItems = allItems.filter(item => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (item._source === 'admin_out') {
      const m = item as AdminMessage;
      return (
        m.subject.toLowerCase().includes(q) ||
        m.message.toLowerCase().includes(q) ||
        `${m.client?.first_name} ${m.client?.last_name}`.toLowerCase().includes(q) ||
        (m.client?.company_name || '').toLowerCase().includes(q)
      );
    } else {
      const t = item as MessageThread;
      return (
        t.subject.toLowerCase().includes(q) ||
        (t.client_name || '').toLowerCase().includes(q) ||
        t.last_message_preview.toLowerCase().includes(q)
      );
    }
  });

  const totalUnread = threads.reduce((sum, t) => sum + t.unread_count, 0);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-14 bg-gray-100 rounded-lg"></div>
          <div className="h-14 bg-gray-100 rounded-lg"></div>
          <div className="h-14 bg-gray-100 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 bg-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {mode !== 'list' && (
              <button
                onClick={goBackToList}
                className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <Mail className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">
              {mode === 'compose' ? 'New Message to Client' :
               mode === 'thread-detail' && selectedThread ? selectedThread.subject :
               mode === 'admin-detail' && expandedAdminMsg ? expandedAdminMsg.subject :
               'Client Messaging'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {totalUnread > 0 && mode === 'list' && (
              <span className="px-2.5 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">
                {totalUnread} unread
              </span>
            )}
            {mode === 'list' && (
              <button
                onClick={() => setMode('compose')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                New Message
              </button>
            )}
            {mode === 'thread-detail' && selectedThread && (
              <button
                onClick={() => handleToggleStatus(selectedThread)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  selectedThread.status === 'open'
                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                {selectedThread.status === 'open' ? (
                  <><Clock className="w-3.5 h-3.5" />Close Thread</>
                ) : (
                  <><CheckCircle className="w-3.5 h-3.5" />Reopen Thread</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Compose new direct message */}
      {mode === 'compose' && (
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">To</label>
            <select
              value={newToClient}
              onChange={e => setNewToClient(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Select a client...</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.company_name} — {c.first_name} {c.last_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Subject</label>
            <input
              type="text"
              value={newSubject}
              onChange={e => setNewSubject(e.target.value)}
              placeholder="Message subject..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Message</label>
            <textarea
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              rows={7}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSendDirect}
              disabled={sending || !newToClient || !newSubject.trim() || !newMessage.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Message'}
            </button>
            <button
              onClick={goBackToList}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* Admin message detail (outbound direct message) */}
      {mode === 'admin-detail' && expandedAdminMsg && (
        <div className="flex flex-col">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">
                      {expandedAdminMsg.client?.first_name} {expandedAdminMsg.client?.last_name}
                    </span>
                    {expandedAdminMsg.client?.title && (
                      <span className="text-xs text-gray-500">{expandedAdminMsg.client.title}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {expandedAdminMsg.client?.company_name && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Building2 className="w-3 h-3" />{expandedAdminMsg.client.company_name}
                      </span>
                    )}
                    {expandedAdminMsg.client?.phone && (
                      <span className="text-xs text-gray-500">{expandedAdminMsg.client.phone}</span>
                    )}
                  </div>
                </div>
              </div>
              {expandedAdminMsg.read && (
                <span className="flex items-center gap-1 text-xs text-green-600 flex-shrink-0 mt-1">
                  <CheckCheck className="w-3.5 h-3.5" />Read
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <Clock className="w-3 h-3" />
              Sent {formatDateFull(expandedAdminMsg.created_at)}
              {expandedAdminMsg.read_at && (
                <span className="ml-2">· Read {formatDateFull(expandedAdminMsg.read_at)}</span>
              )}
            </div>
          </div>
          <div className="p-5 max-h-80 overflow-y-auto">
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{expandedAdminMsg.message}</p>
          </div>
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-between">
            <button
              onClick={() => deleteAdminMsg(expandedAdminMsg.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
            <button
              onClick={goBackToList}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Thread conversation detail */}
      {mode === 'thread-detail' && selectedThread && (
        <div className="flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">{selectedThread.client_name}</span>
                    {selectedThread.client_title && (
                      <span className="text-xs text-gray-500">{selectedThread.client_title}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {selectedThread.client_company && (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Building2 className="w-3 h-3" />{selectedThread.client_company}
                      </span>
                    )}
                    {selectedThread.client_phone && (
                      <span className="text-xs text-gray-500">{selectedThread.client_phone}</span>
                    )}
                    {selectedThread.client_email && (
                      <span className="text-xs text-gray-500">{selectedThread.client_email}</span>
                    )}
                  </div>
                </div>
              </div>
              <span className="flex items-center gap-1 text-xs flex-shrink-0 mt-1">
                {selectedThread.status === 'open' ? (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-3 h-3" />Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-gray-400">
                    <Clock className="w-3 h-3" />Closed
                  </span>
                )}
              </span>
            </div>
          </div>

          <div className="p-4 h-80 overflow-y-auto space-y-3">
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                Loading messages...
              </div>
            ) : threadMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No messages yet
              </div>
            ) : (
              threadMessages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-xl px-4 py-3 ${
                    msg.sender_type === 'admin'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold opacity-80">
                        {msg.sender_type === 'admin' ? 'You (Admin)' : selectedThread.client_name}
                      </span>
                      <span className="text-xs opacity-60">{formatDate(msg.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.message_body}</p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex gap-2">
              <textarea
                value={replyMessage}
                onChange={e => setReplyMessage(e.target.value)}
                placeholder={`Reply to ${selectedThread.client_name}...`}
                rows={2}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white"
              />
              <button
                onClick={handleReply}
                disabled={sending || !replyMessage.trim()}
                className="self-end flex items-center justify-center w-10 h-10 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inbox list */}
      {mode === 'list' && (
        <>
          {/* Search */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search messages..."
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <div className="py-16 text-center">
              <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 text-sm mb-1">
                {searchQuery ? 'No messages match your search' : 'No messages yet'}
              </p>
              {!searchQuery && (
                <button onClick={() => setMode('compose')} className="text-blue-600 text-sm hover:text-blue-800 mt-1 transition-colors">
                  Send a message to a client
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredItems.map(item => {
                const isAdminOut = item._source === 'admin_out';
                const key = isAdminOut ? `admin-${item.id}` : `thread-${item.id}`;
                const isExpanded = expandedRows.has(key);

                if (isAdminOut) {
                  const msg = item as AdminMessage;
                  return (
                    <div key={key} className="bg-white">
                      <div
                        className="flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => openAdminMsg(msg)}
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-teal-50">
                          <Send className="w-4 h-4 text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Sent</span>
                            <span className="text-sm font-semibold text-gray-900 truncate">{msg.subject}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              To: {msg.client?.company_name} — {msg.client?.first_name} {msg.client?.last_name}
                            </span>
                            {msg.read && (
                              <span className="flex items-center gap-0.5 text-xs text-green-600">
                                <CheckCheck className="w-3 h-3" />Read
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  const thread = item as MessageThread;
                  const isUnread = thread.unread_count > 0;
                  return (
                    <div key={key} className={isUnread ? 'bg-blue-50/60' : 'bg-white'}>
                      <div
                        className="flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => openThread(thread)}
                      >
                        <div className="w-2 flex-shrink-0 flex justify-center">
                          {isUnread && <span className="w-2 h-2 bg-blue-600 rounded-full block"></span>}
                        </div>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-50">
                          <MessageSquare className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
                              {thread.subject}
                            </span>
                            {isUnread && (
                              <span className="flex-shrink-0 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {thread.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap">
                            <User className="w-3 h-3 flex-shrink-0" />
                            <span className="font-medium">{thread.client_name}</span>
                            {thread.client_company && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span>{thread.client_company}</span>
                              </>
                            )}
                            {thread.last_message_preview && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="truncate max-w-[160px]">
                                  {thread.last_sender_type === 'admin' ? 'You: ' : ''}
                                  {thread.last_message_preview}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-xs text-gray-400">{formatDate(thread.last_message_at)}</span>
                          {thread.status === 'open' ? (
                            <span className="text-xs text-green-500 flex items-center gap-0.5">
                              <CheckCircle className="w-3 h-3" />Active
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />Closed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
